import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { dataAttrs } from 'Helpers/Testing';
import {
  FDDialog,
  FDDialogActions,
  FDDialogContent,
  FDDialogTitle,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
} from '@facet-wealth/design';
import { BigPicklistInput } from 'Packages/FacetDesign/Components';
import { useSnackbarContext } from 'Context/SnackbarContext';
import fr from 'facet-rest';
import { useRoadmapPaths } from 'Components/UI/guidedHouseholdReview/pathsContext';

const NewRoadmapExplorerPathDialog = ({
  isOpen,
  closeModal,
  onCreated,
}) => {
  const { pathResource: selectedPathResource } = useRoadmapPaths();
  const [submitting, setSubmitting] = useState(false);
  const [selectedCreateMethod, setSelectedCreateMethod] = useState(null);
  const { displayErrorMessage } = useSnackbarContext();

  const handleDuplicateSelectedPath = async () => {
    try {
      setSubmitting(true);
      const [newPathResource] = await selectedPathResource.relationship(fr.relationships.facetPlanDuplicate.name).link('related').get();
      const [newPath] = await newPathResource.submit();
      onCreated(newPath.id());
    } catch (e) {
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to create new path');
    } finally {
      setSubmitting(false);
      closeModal();
    }
  };

  const handleCreateNewPath = async () => {
    try {
      setSubmitting(true);
      const newPathCollection = await selectedPathResource.relationship(fr.relationships.newPath.name).link('related').get();

      const newPathResource = newPathCollection.create();
      newPathResource.attr('name', 'New Path');
      const [newPath] = await newPathResource.submit();
      onCreated(newPath.id());
    } catch (e) {
      displayErrorMessage(e, e.errors[0]?.detail || 'Unable to create new path');
    } finally {
      setSubmitting(false);
      closeModal();
    }
  };

  const applyCreateNewPathChoice = async () => {
    switch (selectedCreateMethod) {
      case 'duplicate':
        await handleDuplicateSelectedPath();
        break;
      case 'create-new':
        await handleCreateNewPath();
        break;
      default:
        displayErrorMessage('Please select an option');
    }
  };

  return (
    <FDDialog
      dialogWidth={'sm'}
      fullScreen={false}
      onClose={closeModal}
      open={isOpen}
      color={'subtle'}
      {...dataAttrs('new-message-template-dialog')}
    >
      <FDDialogTitle value={'Create new path'} />
      <FDDialogContent>
        <BigPicklistInput
          onChange={(newValue) => {
            setSelectedCreateMethod(newValue);
          }}
          options={[
            {
              disabled: false,
              id: 'duplicate',
              prompt: 'Duplicate this path',
            },
            {
              disabled: false,
              id: 'create-new',
              prompt: 'Start from scratch',
            },
          ]}
          value={selectedCreateMethod || ''}
        />
      </FDDialogContent>
      <FDDialogActions>
        <FDSecondaryActionButton
          onClick={closeModal}
          test={'new-path-cancel-btn'}
          label={'Cancel'}
          size={'sm'}
        />
        <FDPrimaryActionButton
          onClick={() => applyCreateNewPathChoice()}
          disabled={selectedCreateMethod === null}
          loading={submitting}
          test={'new-path-apply-selected-create-option-btn'}
          label={'Create'}
          size={'sm'}
        />
      </FDDialogActions>
    </FDDialog>
  );
};

export default NewRoadmapExplorerPathDialog;
NewRoadmapExplorerPathDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
